import React, { useState } from 'react';
import { fmt, fmtK } from '../../utils/formatters';
import {
  CallOutcome, OUTCOME_WORDS, RecordCall, byDate, callOutcome, callsSentence, dayLabel, moneyRangeWords, shortDay,
} from '../../utils/weather';
import { TIP_CLASS, useWidth } from './weatherUi';

// Expected against actual: one row per scored call, on one money axis of
// "dollars against the usual day". The usual day is a tick on the zero line,
// the expected range a plum whisker, what the day sold a dot (forest right,
// dusty rose missed, hollow when the weather never came). It shows how big
// the misses were, which "Right 7 of 9" hides.

const PLUM = '#4A2D5E';
const INK = '#2D2A2E';
const INK_SOFT = '#6B6670';
const HAIRLINE = '#E6E2DE';
export const OUTCOME_DOT: Record<CallOutcome, string> = { right: '#2E6B4F', missed: '#B54A4A', never: '#FFFFFF', open: '#FFFFFF' };

const LABEL_W = 48;
const PAD_R = 12;
const TOP = 6;
const ROW_H = 26;
const AXIS_H = 22;

/** A round step that gives three to five ticks across the span. */
function step(span: number): number {
  const raw = span / 4;
  const mag = 10 ** Math.floor(Math.log10(raw));
  return [1, 2, 2.5, 5, 10].map((k) => k * mag).find((s) => s >= raw) ?? 10 * mag;
}

/** "usual" at zero; signed compact money elsewhere: "−$200", "+$100". */
const tickLabel = (v: number) => (v === 0 ? 'usual' : v > 0 ? `+${fmtK(v)}` : fmtK(v));

const sentenceCase = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

export default function CallsChart({ calls }: { calls: RecordCall[] }) {
  const [ref, width] = useWidth<HTMLDivElement>(320);
  const [active, setActive] = useState<string | null>(null);
  const rows = byDate(calls.filter((c) => c.scored && c.actual != null)).reverse();
  if (!rows.length) return null;

  const values = rows.flatMap((c) => [c.dollars_low, c.dollars_high, (c.actual as number) - c.usual]).concat(0);
  const rawLo = Math.min(...values);
  const rawHi = Math.max(...values);
  const s = step(Math.max(1, rawHi - rawLo));
  const lo = Math.floor((rawLo - s * 0.25) / s) * s;
  const hi = Math.ceil((rawHi + s * 0.25) / s) * s;
  const x0 = LABEL_W + 8;
  const px = (v: number) => x0 + ((v - lo) / (hi - lo)) * (width - x0 - PAD_R);
  const ticks: number[] = [];
  for (let t = lo; t <= hi + 1e-9; t += s) ticks.push(Math.round(t));
  const height = TOP + rows.length * ROW_H + AXIS_H;
  const tip = rows.find((c) => c.for_date === active);
  const tipIndex = tip ? rows.indexOf(tip) : 0;

  return (
    <div data-calls-chart>
      <div aria-hidden="true" className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1 font-sans text-[10px] font-medium text-[#6B6670]">
        <span className="inline-flex items-center gap-1.5"><span className="h-0.5 w-4 bg-[#4A2D5E]" />expected range</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-3 w-0.5 bg-[#6B6670]" />a usual day</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-[#2E6B4F]" />sold, right</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-[#B54A4A]" />sold, missed</span>
        {rows.some((c) => callOutcome(c) === 'never') && (
          <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full border border-[#6B6670]" />the weather never came</span>
        )}
      </div>
      <div ref={ref} className="relative w-full">
        <svg
          role="img" aria-label={callsSentence(calls)} width={width} height={height} viewBox={`0 0 ${width} ${height}`}
          className="block h-auto max-w-full overflow-visible font-sans"
        >
          {ticks.map((t) => (
            <g key={t}>
              <line x1={px(t)} x2={px(t)} y1={TOP - 4} y2={TOP + rows.length * ROW_H} stroke={t === 0 ? INK : HAIRLINE} strokeWidth={1} />
              <text x={px(t)} y={height - 6} textAnchor="middle" fontSize={10} fill={INK_SOFT} className="tabular-nums">{tickLabel(t)}</text>
            </g>
          ))}
          {rows.map((c, i) => {
            const y = TOP + i * ROW_H + ROW_H / 2;
            const outcome = callOutcome(c);
            const [a, b] = [Math.min(c.dollars_low, c.dollars_high), Math.max(c.dollars_low, c.dollars_high)];
            const dim = active != null && active !== c.for_date;
            return (
              <g key={c.for_date} data-row={c.for_date} opacity={dim ? 0.45 : 1}>
                <text x={0} y={y + 3.5} fontSize={11} fill={INK_SOFT} className="tabular-nums">{shortDay(c.for_date)}</text>
                <line data-whisker x1={px(a)} x2={px(b)} y1={y} y2={y} stroke={PLUM} strokeWidth={2} />
                <line x1={px(a)} x2={px(a)} y1={y - 4} y2={y + 4} stroke={PLUM} strokeWidth={1.5} />
                <line x1={px(b)} x2={px(b)} y1={y - 4} y2={y + 4} stroke={PLUM} strokeWidth={1.5} />
                <line data-usual x1={px(0)} x2={px(0)} y1={y - ROW_H / 2} y2={y + ROW_H / 2} stroke={INK} strokeWidth={1.5} />
                <circle
                  data-actual cx={px((c.actual as number) - c.usual)} cy={y} r={4} fill={OUTCOME_DOT[outcome]}
                  stroke={outcome === 'never' ? INK_SOFT : '#FFFFFF'} strokeWidth={outcome === 'never' ? 1.5 : 2}
                />
                <rect
                  data-hit x={0} y={y - ROW_H / 2} width={width} height={ROW_H} fill="transparent"
                  onMouseEnter={() => setActive(c.for_date)} onMouseLeave={() => setActive(null)}
                  onClick={() => setActive(active === c.for_date ? null : c.for_date)}
                />
              </g>
            );
          })}
        </svg>
        {tip && (
          <div
            data-tip aria-hidden="true" className={`${TIP_CLASS} absolute w-[200px]`}
            style={{ left: Math.max(0, Math.min(width - 200, x0)), top: TOP + (tipIndex + 1) * ROW_H + 2 }}
          >
            <div className="font-medium">{dayLabel(tip.for_date)} · {sentenceCase(OUTCOME_WORDS[callOutcome(tip)])}</div>
            <div className="tabular-nums text-[#6B6670]">
              Expected {moneyRangeWords(tip.usual + tip.dollars_low, tip.usual + tip.dollars_high)}
            </div>
            <div className="tabular-nums">Sold {fmt(tip.actual as number)} · usual {fmt(tip.usual)}</div>
          </div>
        )}
      </div>
    </div>
  );
}
